import * as React from 'react';
import { numericOnly } from '../InvoicingUtils';

// `locked` marks an item whose amount is fully calculated (hourly rate × confirmed hours — see
// isGcpRateFullySet) and therefore shown read-only: no caller renders an input for it.
export type PriceableGcpItem = { id: string; defaultAmount: number; locked?: boolean };

// What an item is actually priced at, for both the totals on screen and the submit payload.
//
// A locked item always bills its calculated amount. It has no input anywhere in the UI, so an
// override read here could only be a value this hook seeded earlier — under an id that has since
// come to mean a different mission, or before the rate arrived — and it would then silently
// outrank the amount the row displays. Keeping the two in one function is the point: a price shown
// and a price billed that are derived separately are free to disagree, which is precisely how an
// invoice once went out billing amounts no row had ever displayed.
export function resolveGcpItemPrice(override: string | undefined, defaultAmount: number, locked = false): number {
  if (locked) return defaultAmount;

  return parseFloat(override ?? String(defaultAmount)) || 0;
}

// Shared "default a GCP item's price to its backend-calculated amount, track per-item
// overrides, and report whether any item was actually edited" mechanism used by both
// the pilot submission form (fixed-fee and time & materials) and the office invoice
// review/fix form. Callers own their own item shape and any per-group/per-section
// aggregation — this hook only owns the price map itself.
export function useGcpItemPrices(items: PriceableGcpItem[]) {
  const [prices, setPrices] = React.useState<Record<string, string>>({});
  const defaultPricesRef = React.useRef<Record<string, string>>({});

  const lockedById = React.useMemo(() => {
    const map = new Map<string, boolean>();
    for (const item of items) map.set(item.id, item.locked === true);
    return map;
  }, [items]);

  React.useEffect(() => {
    if (items.length === 0) return;
    setPrices((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of items) {
        // A locked item is never edited, so it needs no override to fall back on — and seeding one
        // would only leave a stale value behind for whatever ends up under this id later.
        if (item.locked) continue;
        if (next[item.id] === undefined) {
          const defaultValue = String(item.defaultAmount);
          next[item.id] = defaultValue;
          defaultPricesRef.current[item.id] = defaultValue;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items]);

  const updatePrice = React.useCallback(
    (id: string, value: string) => setPrices((prev) => ({ ...prev, [id]: numericOnly(value) })),
    [],
  );

  const getPrice = React.useCallback(
    (id: string, fallbackAmount: number) => resolveGcpItemPrice(prices[id], fallbackAmount, lockedById.get(id)),
    [prices, lockedById],
  );

  const isDirty = items.some(
    (item) =>
      !item.locked &&
      prices[item.id] !== undefined &&
      prices[item.id] !== (defaultPricesRef.current[item.id] ?? String(item.defaultAmount)),
  );

  const total = React.useMemo(
    () => items.reduce((s, item) => s + getPrice(item.id, item.defaultAmount), 0),
    [items, getPrice],
  );

  return { prices, updatePrice, getPrice, isDirty, total };
}
